import { Add as AddIcon } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, DialogTitle, FormControlLabel, FormGroup, Switch } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { ContactType } from "@repo/server-api";
import { useBoundDeep } from "@repo/shared/common";
import { BoundAsyncSelect, useAsyncSelectContactQuery } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";

import { TeamMemberPositionAssignmentTable } from "../components/TeamMemberPositionAssignmentTable";
import { useAsyncSelectEmployeeWithPriceQuery } from "../hooks/AsyncSelectEmployeeWithPriceQuery";
import { useProjectTeamMemberValidator } from "../hooks/ProjectValidationHooks";
import { useSaveProjectAdditionalTeamMemberInformation } from "../hooks/SaveProjectAdditionalTeamMemberInformationHook";
import { createEmptyTeamMember } from "../types/ProjectTypes";
import {
	DialogAddPositionsToTeamMember,
	type DialogAddPositionsToTeamMemberHandle,
} from "./DialogAddPositionsToTeamMember";

import type { ProjectPosition, ProjectTeamMember, WorksOnPosition } from "../types/ProjectTypes";

export type DialogProjectTeamMemberProps = Readonly<{
	onSaved: (teamMember: ProjectTeamMember) => void;
}>;

export type DialogProjectTeamMemberArgs = {
	projectId: string;
	positions: ProjectPosition[];
	teamMember?: ProjectTeamMember | null;
};

export type DialogProjectTeamMemberHandle = {
	show: (args: DialogProjectTeamMemberArgs) => void;
};

export const DialogProjectTeamMember = forwardRef<
	Readonly<DialogProjectTeamMemberHandle>,
	Readonly<DialogProjectTeamMemberProps>
>((props, ref) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [projectId, setProjectId] = useState<string>("");
	const [positions, setPositions] = useState<ProjectPosition[]>([]);
	const [teamMember, setTeamMember] = useState<ProjectTeamMember>(createEmptyTeamMember());
	const [worksOnPositions, setWorksOnPositions] = useState<WorksOnPosition[]>([]);
	const { errors, setErrors, validate } = useProjectTeamMemberValidator();
	const addPositionsDialogRef = useRef<DialogAddPositionsToTeamMemberHandle | null>(null);

	// Bindings
	const useBinding = <TValue,>(selector: (m: ProjectTeamMember) => TValue) => {
		return useBoundDeep(teamMember, setTeamMember, selector, errors, setErrors);
	};

	const availableEmployees = useAsyncSelectEmployeeWithPriceQuery(
		[ContactType.Freelancer, ContactType.Employee],
		false,
	);

	const teamMemberField = useBinding((p) => p.teamMember);
	const contractorField = useBinding((p) => p.contractor);
	const searchContractorHook = useAsyncSelectContactQuery([ContactType.Contractor]);

	const onSaved = (savedTeamMember: ProjectTeamMember) => {
		// Insert Id into position
		setTeamMember(savedTeamMember);

		props.onSaved(savedTeamMember);
		setOpen(false);
	};

	const { saveProjectTeamMember, loading } = useSaveProjectAdditionalTeamMemberInformation({
		projectId,
		onSaved,
	});

	useImperativeHandle(ref, () => ({
		show: (args: DialogProjectTeamMemberArgs): void => {
			setProjectId(args.projectId);
			setPositions(args.positions);
			availableEmployees.resetSearch();
			searchContractorHook.resetSearch();

			if (args.teamMember) {
				setTeamMember(args.teamMember);
				setWorksOnPositions(args.teamMember.worksOnPositions || []);
			} else {
				setTeamMember(createEmptyTeamMember());
				setWorksOnPositions([]);
			}

			setErrors({});
			setOpen(true);
		},
	}));

	const handleClose = () => {
		setOpen(false);
		setErrors({});
	};

	const handleAddPositions = (selectedPositionIds: string[]) => {
		const newAssignments: WorksOnPosition[] = selectedPositionIds
			.filter((id) => !worksOnPositions.some((a) => a.positionId === id))
			.map((id) => ({
				positionId: id,
				pricePerHour: teamMember.teamMember?.pricePerHour ?? null,
				hours: null,
			}));

		setWorksOnPositions([...worksOnPositions, ...newAssignments]);
	};

	const handleSubmit = async () => {
		const teamMemberWithPositions: ProjectTeamMember = {
			...teamMember,
			worksOnPositions,
		};

		const result = await validate(teamMemberWithPositions);
		console.log("Validation result:", result);
		if (!result.isValid || loading) return;

		// save team member
		saveProjectTeamMember(teamMemberWithPositions);
	};

	return (
		<Dialog open={open} maxWidth="lg" fullWidth data-testid="team-member-dialog">
			<DialogTitle>{teamMember?.id ? t("edit-team-member") : t("new-team-member")}</DialogTitle>
			<DialogContent>
				<Box className="fullCenter" sx={{ mt: 2.5 }}>
					<BoundAsyncSelect
						label={t("member")}
						boundedValue={teamMemberField}
						searchHook={availableEmployees}
						additionalProps={{ className: "w-100 left" }}
					/>
				</Box>
				<Box className="fullCenter">
					<FormGroup className="padding7">
						<FormControlLabel
							control={<Switch readOnly checked={teamMember?.teamMember?.isFreelancer ?? false} />}
							label={t("is-freelancer")}
						/>
					</FormGroup>
				</Box>

				<Box className="fullCenter">
					<BoundAsyncSelect
						label={t("is-employed-by")}
						boundedValue={contractorField}
						searchHook={searchContractorHook}
						additionalProps={{ className: "w-100 left" }}
					/>
				</Box>

				<Box className="fullCenter" sx={{ mt: 2.5 }}>
					<Button
						variant="outlined"
						startIcon={<AddIcon />}
						onClick={() => {
							const unassignedPositions = positions.filter(
								(p) => !worksOnPositions.some((a) => a.positionId === p.id),
							);
							addPositionsDialogRef.current?.show({
								availablePositions: unassignedPositions,
								onConfirm: handleAddPositions,
							});
						}}
						fullWidth
						data-testid="add-positions-button">
						{t("add-positions")}
					</Button>
				</Box>

				<TeamMemberPositionAssignmentTable
					assignments={worksOnPositions}
					availablePositions={positions}
					onAssignmentsChange={setWorksOnPositions}
					validationErrors={errors.worksOnPositions}
				/>

				<DialogAddPositionsToTeamMember ref={addPositionsDialogRef} />
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" onClick={handleClose} autoFocus data-testid="team-member-cancel-button">
					{t("cancel")}
				</Button>
				<LoadingButton
					loading={loading}
					variant="contained"
					onClick={handleSubmit}
					className={loading ? "" : "blueButton"}
					data-testid="team-member-save-button">
					{t("save")}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
});

DialogProjectTeamMember.displayName = "DialogProjectTeamMember";
